import { Link } from "react-router-dom";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";

const FEATURED_ID = "7820420939837";

export default function FeaturedProduct() {
  const { products, productNodes, loading } = useShopifyProducts();

  const product = products.find((p) => p.id === FEATURED_ID);
  const node = productNodes[FEATURED_ID];

  const imageUrl = node?.images.edges[0]?.node.url || product?.image || "";
  const highlights = product?.highlights.slice(0, 4) ?? [];
  const price = product?.price;
  const originalPrice = product?.originalPrice;
  const name = product?.name ?? "Featured Product";
  const brand = product?.brand ?? "";

  return (
    <section className="bg-gray-950 py-20 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest text-center mb-3">
          Featured Product
        </p>

        {loading ? (
          <div className="animate-pulse">
            <div className="h-8 w-72 bg-gray-800 rounded mx-auto mb-12" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="w-full h-80 md:h-96 bg-gray-800 rounded-lg" />
              <div className="space-y-4">
                <div className="h-4 bg-gray-800 rounded w-full" />
                <div className="h-4 bg-gray-800 rounded w-5/6" />
                <div className="h-4 bg-gray-800 rounded w-4/6" />
                <div className="h-10 bg-gray-800 rounded w-40 mt-6" />
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white text-center mb-2">
              {name}
            </h2>
            {brand && (
              <p className="text-gray-400 text-sm text-center mb-12">by {brand}</p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Product image */}
              <div className="relative rounded-lg overflow-hidden w-full h-80 md:h-96 bg-white/5 flex items-center justify-center">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={name}
                    className="w-full h-full object-contain p-8"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 rounded-lg" />
                )}
                <div className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  Best Seller
                </div>
                {originalPrice && (
                  <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                    Sale
                  </div>
                )}
              </div>

              {/* Details */}
              <div>
                {/* Price */}
                {price !== undefined && (
                  <div className="flex items-baseline gap-3 mb-5">
                    <span className="text-3xl font-extrabold text-white">
                      ${price.toLocaleString()}
                    </span>
                    {originalPrice && (
                      <span className="text-lg text-gray-500 line-through">
                        ${originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-gray-300 text-base leading-relaxed mb-6">
                  High-capacity, expandable, and built for real home backup without the noise, fumes,
                  and maintenance of a traditional gas generator. Delivers reliable clean energy for
                  days, not hours — backed by full manufacturer warranty.
                </p>

                {highlights.length > 0 && (
                  <ul className="space-y-3 mb-8">
                    {highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-check-line text-amber-400 text-base"></i>
                        </div>
                        <span className="text-gray-300 text-sm">{h}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/shop/${FEATURED_ID}`}
                    className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-7 py-3 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    View Product Details
                  </Link>
                  <Link
                    to="/shop"
                    className="border border-gray-600 hover:border-gray-400 text-gray-300 font-semibold px-7 py-3 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    See All Solar Systems
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
